import { getPubSub, PubSub } from 'DVWebNodeUtils'
import { loadWebPlayer } from './loader'
import {
  WebPlayer,
  WebPlayerEvent,
  PlayerConfig,
  PlayerCapabilities,
} from 'ATVWebPlayer'
import { PlayerConfigData, getPlayerConfig } from './config'
import { PlayerContentConfig } from '..'
import { Action, logAction } from './logger'

type PlayerEvent = WebPlayerEvent | 'play' | 'stop' | 'beforePlay'
export type HistoryItem = PlayerContentConfig & { startEpoch?: number }

const eventsToListen: WebPlayerEvent[] = ['closed', 'asinChanged', 'aborted']
const eventsToLog: Action[] = ['play', 'closed', 'asinChanged']

const sanitizeContentConfig = (
  args: PlayerContentConfig
): PlayerContentConfig => ({
  trailer: args.trailer,
  position: Number(args.position || 0), //position is a Long in the WPS model, so can come back as a String in the AJAX call
  videoType: args.videoType,
  live: Boolean(args.lcid),
  asin: args.lcid || args.asin,
  ref: args.ref,
})

const addStartEpoch = (args: PlayerContentConfig) => ({
  ...args,
  startEpoch: Math.floor(new Date().getTime() / 1000),
})

const isLivePlaybackRequest = (playbackArgs: PlayerContentConfig) =>
  playbackArgs.videoType === 'Live' || playbackArgs.lcid || playbackArgs.live

export class PlayerProxy {
  readonly player: WebPlayer
  readonly liveIncompatibleCallback: () => void
  private readonly playerConfig: PlayerConfig
  private pubSub: PubSub
  private lastTitle: HistoryItem | null
  public readonly capabilities: Promise<PlayerCapabilities>
  constructor(
    player: WebPlayer,
    playerConfig: PlayerConfigData,
    liveIncompatibleCallback: () => void
  ) {
    this.player = player
    this.liveIncompatibleCallback = liveIncompatibleCallback
    this.pubSub = getPubSub()
    this.playerConfig = getPlayerConfig(playerConfig)
    this.lastTitle = null
    this.init()
    this.capabilities = new Promise<PlayerCapabilities>(resolve => {
      player.on(
        'playerCapabilities',
        ({ capabilities }: { capabilities: PlayerCapabilities }) => {
          resolve(capabilities)
        }
      )
    })
  }

  private init() {
    eventsToListen.forEach(event => {
      this.player.on(event, async args => {
        await this.callAction(event)
        if (args && event === 'asinChanged') {
          this.lastTitle = addStartEpoch(sanitizeContentConfig(args))
        }
      })
    })

    eventsToLog.forEach(event => {
      this.on(event, logAction(event))
    })

    this.on('beforePlay', (args: PlayerContentConfig) =>
      this.liveIncompatibleCheck(args)
    )

    this.player.initialize(this.playerConfig)
  }

  private async callAction(event: PlayerEvent) {
    return await Promise.all(this.pubSub.query(event).map(cb => cb(this.lastTitle)))
  }

  public on(
    event: PlayerEvent,
    handler: (content: PlayerContentConfig) => void
  ) {
    this.pubSub.subscribe(event, handler)
  }

  public async prepare(config: PlayerContentConfig) {
    await this.callAction('prepare')
    this.player.prepare(this.playerConfig, sanitizeContentConfig(config))
  }

  private async liveIncompatibleCheck(content: PlayerContentConfig) {
    const liveCompatible = isLivePlaybackRequest(content)
      ? (await this.capabilities).supportsLive
      : true
    if (!liveCompatible) {
      this.liveIncompatibleCallback()
      throw 'Live Incompatible'
    }
  }

  public async play(config: PlayerContentConfig) {
    const contentConfig = sanitizeContentConfig(config)
    this.lastTitle = addStartEpoch(contentConfig)
    await this.callAction('beforePlay')
    await this.callAction('play')
    const playbackConfig = {
      clickstreamRefMarker: config.ref || 'atv_dp_unknown',
      playbackInitiatedTime: new Date().getTime(),
    }
    this.player.play(this.playerConfig, contentConfig, playbackConfig)
  }

  public async stop() {
    await this.callAction('stop')
    this.player.initialize(this.playerConfig)
    await this.callAction('closed')
  }

  public resetPlayer() {
    this.pubSub = getPubSub()
  }
}

export const playerPromise = { instance: null } as {
  instance: Promise<PlayerProxy> | null
}

export const createPlayer = (
  playerConfig: PlayerConfigData,
  liveIncompatibleCallback: () => void
) => {
  playerPromise.instance = playerPromise.instance || loadWebPlayer().then(
    (webPlayer: WebPlayer) =>
      new PlayerProxy(webPlayer, playerConfig, liveIncompatibleCallback)
  )
  return playerPromise.instance
}
